import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContextFaq {
  id: string;
  question: string;
  answer: string;
  module_key: string;
  page_key: string | null;
  action_key: string | null;
  audience: string;
  sort_order: number;
  is_featured: boolean;
  related_link: string | null;
}

export function useContextFaqs(moduleKey: string, pageKey?: string, actionKey?: string) {
  return useQuery({
    queryKey: ["context-faqs", moduleKey, pageKey, actionKey],
    queryFn: async () => {
      // Fetch all active FAQs for this module in one query
      const { data, error } = await supabase
        .from("context_faqs" as any)
        .select("*")
        .eq("module_key", moduleKey)
        .eq("status", "active")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      const all = (data || []) as unknown as ContextFaq[];

      // Priority matching: exact → module+page → module-level
      if (actionKey) {
        const exact = all.filter(
          (f) => f.page_key === pageKey && f.action_key === actionKey
        );
        if (exact.length >= 2) return exact;
      }

      if (pageKey) {
        const pageFaqs = all.filter(
          (f) => f.page_key === pageKey && !f.action_key
        );
        if (pageFaqs.length >= 2) return pageFaqs;
        // Include action-level FAQs for this page too
        const pageAll = all.filter((f) => f.page_key === pageKey);
        if (pageAll.length >= 2) return pageAll;
      }

      // Fallback: all module FAQs
      return all;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminContextFaqs(filters?: {
  moduleKey?: string;
  audience?: string;
  status?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ["admin-context-faqs", filters],
    queryFn: async () => {
      let query = supabase
        .from("context_faqs" as any)
        .select("*")
        .order("module_key")
        .order("page_key")
        .order("sort_order", { ascending: true });

      if (filters?.moduleKey) query = query.eq("module_key", filters.moduleKey);
      if (filters?.audience) query = query.eq("audience", filters.audience);
      if (filters?.status) query = query.eq("status", filters.status);
      if (filters?.search)
        query = query.ilike("question", `%${filters.search}%`);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as (ContextFaq & {
        status: string;
        created_at: string;
        updated_at: string;
      })[];
    },
  });
}
